"use client";
import {JSX, useEffect, useState} from "react";

import Link from "next/link";
import {usePathname, useRouter} from "next/navigation";

import type {DestinationCardData} from "@/entities/destination/model";

import {useModel} from "@/shared/hooks";
import {useFloatingDropdown} from "@/shared/hooks/ui/use-floating-dropdown";
import {useTranslation} from "@/shared/i18n";
import {routes} from "@/shared/utils";

import {DestinationSelector} from "./models";

import "./styles/destination-selector.scss";

export interface DestinationSelectorViewProps {
    destinations?: Array<DestinationCardData>;
    label?: string;
    placeholder?: string;
    className?: string;
}

export function DestinationSelectorView({destinations, label, placeholder, className = ""}: DestinationSelectorViewProps): JSX.Element {
    const {t} = useTranslation("common");
    const router = useRouter();
    const pathname = usePathname();
    const normalizePath = (path: string): string => path.replace(/\/$/, "");

    const [model, snap] = useModel(DestinationSelector, {
        destinations,
        onSelect: (destination) => {
            if (normalizePath(destination.url) === normalizePath(pathname)) {
                model.isNavigating = false;
            } else {
                router.push(destination.url);
            }
        },
    });
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
        void model.init();
    }, [model]);

    const {refs, floatingStyles, getReferenceProps, getFloatingProps} = useFloatingDropdown({
        isOpen: snap.isOpen,
        onOpenChange: (open) => {
            if (open) {
                model.open();
            } else {
                model.close();
            }
        },
        placement: "bottom-start",
        offsetValue: 8,
        shiftPadding: 8,
        enableAutoScroll: true,
        maxHeightPadding: 16,
        maxHeight: 400, // Limit dropdown height to 400px
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                model.moveHighlightDown();
                break;
            case "ArrowUp":
                event.preventDefault();
                model.moveHighlightUp();
                break;
            case "Enter":
                event.preventDefault();
                model.onEnter();
                break;
            case "Escape":
                event.preventDefault();
                model.close();
                break;
        }
    };

    const handleItemClick = (index: number): void => {
        model.highlightedIndex = index;
        model.selectHighlighted();
    };

    const referenceProps = isHydrated
        ? getReferenceProps({
              onKeyDown: handleKeyDown,
              "aria-expanded": snap.isOpen,
              "aria-haspopup": "listbox",
              "aria-activedescendant": snap.isOpen && snap.highlightedIndex >= 0 ? `destination-${snap.highlightedIndex}` : undefined,
          })
        : {};

    return (
        <div className={`destination-selector ${className}`}>
            <div className="destination-selector__header">
                {label && <div className="destination-selector__label">{label}</div>}
                <Link href={routes.destinations.franchise.url()} className="destination-selector__link">
                    {t("viewAll")}
                </Link>
            </div>

            <div className="destination-selector__search-container">
                <input
                    type="text"
                    ref={isHydrated ? refs.setReference : undefined}
                    className="destination-selector__input"
                    placeholder={placeholder || t("choiceDestination")}
                    value={snap.searchInput.value}
                    onChange={(e) => model.onSearchChange(e.target.value)}
                    onFocus={() => model.onFocus()}
                    onClick={() => model.onFocus()}
                    disabled={snap.isNavigating}
                    {...referenceProps}
                />
                <button
                    type="button"
                    className="destination-selector__enter-button"
                    onClick={() => model.onEnter()}
                    disabled={snap.isNavigating || !snap.selectedDestination}
                >
                    {snap.isNavigating ? (
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="destination-selector__spinner"
                        >
                            <path d="M8 0V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M8 12V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M0 8H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M12 8H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M2.343 2.343L5.172 5.172" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M10.828 10.828L13.657 13.657" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M2.343 13.657L5.172 10.828" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            <path d="M10.828 5.172L13.657 2.343" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Autocomplete dropdown - only render after hydration to prevent SSR mismatch */}
            {isHydrated && (
                <div
                    ref={refs.setFloating}
                    style={snap.isOpen ? floatingStyles : {display: "none"}}
                    className={`destination-selector__dropdown ${snap.isOpen ? "is-open" : ""}`}
                    role="listbox"
                    {...getFloatingProps()}
                >
                    <div className="destination-selector__grid">
                        {snap.filteredDestinations.map((destination, index) => (
                            <button
                                key={destination.slug}
                                id={`destination-${index}`}
                                type="button"
                                className={`destination-selector__item ${index === snap.highlightedIndex ? "is-highlighted" : ""}`}
                                role="option"
                                aria-selected={index === snap.highlightedIndex}
                                onClick={() => handleItemClick(index)}
                            >
                                {destination.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
